import styled from "@emotion/styled";
import { useState } from "react";

import { ConfirmationDialog, ContextMenu, Icon, MenuItem } from "@skilitics/designer-ui-components";
import { useFormatMessage, type FormatMessages } from "@skilitics/localization";

import { FrameEventTypes, useEventPublish } from "../events";
import { NavigationThumbnail, type NavigationThumbnailProps } from "./NavigationThumbnail";
import { ThumbnailMenu } from "./Thumbnail";

const MenuItemContainer = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 10px;

  height: 100%;
`;

type Props = NavigationThumbnailProps & {
  menu: boolean;
};

export const FrameNavigationThumbnail = ({ node, title, selected = false, menu }: Props) => {
  const formatMessage = useFormatMessage(formatStrings);

  const publish = useEventPublish();

  const [open, setOpen] = useState(false);

  const handleDelete = () => publish(FrameEventTypes.REMOVE_FRAME_REQUESTED, { frameId: node.id });

  return (
    <NavigationThumbnail node={node} title={title || formatMessage("fallbackTitle")} selected={selected}>
      {menu && (
        <ThumbnailMenu>
          <ContextMenu data-test="frame-thumbnail-context-menu">
            <MenuItem key="remove" onClick={() => setOpen(true)}>
              <MenuItemContainer>
                <Icon icon="trash-alt" />
                <span>{formatMessage("delete")}</span>
              </MenuItemContainer>
            </MenuItem>
          </ContextMenu>
        </ThumbnailMenu>
      )}
      <ConfirmationDialog
        open={open}
        header={formatMessage("header")}
        message={formatMessage("message")}
        confirmLabel={formatMessage("confirm")}
        cancelLabel={formatMessage("cancel")}
        onConfirm={(confirmed) => {
          setOpen(false);

          if (confirmed) {
            handleDelete();
          }
        }}
      />
    </NavigationThumbnail>
  );
};

const formatStrings = {
  fallbackTitle: {
    en: "Frame",
  },
  delete: {
    en: "Delete",
  },
  header: {
    en: "Delete the frame?",
  },
  message: {
    en: "Are you sure you want to delete this frame? Activities will render full-screen again. This cannot be undone.",
  },
  confirm: {
    en: "Yes, delete it",
  },
  cancel: {
    en: "No, keep it",
  },
} satisfies FormatMessages;
